/*
Description: Comprise Health cascading dropdown option lists
Date: 09/03/2026
Author: Comprehend PT

Transcribed from the EMR's own Angular bundle (chunk-2YPPN7LJ.js). Each map's
top-level keys were verified against the live parent dropdown's options — same
strings, same order — so these are the lists the EMR itself resolves against:
  ROM      `gme`, reached through `aC(joint)`, which falls back to a generic
           12-movement list when no joint is selected — which is why scanning the
           page mid-note can hand back movements belonging to no joint at all.
  MMT      the region/movements/muscles array behind onRegionChange /
           onMovementChange. A movement with a single muscle renders no muscle
           picker in the EMR at all.
  Special  `specialTestsByRegion`.

Why this file exists: a dependent dropdown only ever holds the *currently
selected* parent's children, so scanning can never enumerate it. With the full
map we hand the AI a parent-specific list instead of asking it to free-text a
name and hoping the fill can match it.

Keep in sync with the EMR. A name we send that Comprise has since renamed shows
up as an unmatched dropdown in the fill's error list, not as a wrong value.
*/


/** ROM: joint -> the movements that joint's dropdown offers. */
export const ROM_JOINT_MOVEMENTS: Record<string, string[]> = {
  Shoulder: ['Flexion', 'Extension', 'Abduction', 'Adduction', 'External Rotation (ER)', 'Internal Rotation (IR)', 'Horizontal Abduction', 'Horizontal Adduction', 'Scaption (optional)'],
  Elbow: ['Flexion', 'Extension'],
  Forearm: ['Supination', 'Pronation'],
  Wrist: ['Flexion', 'Extension', 'Radial Deviation', 'Ulnar Deviation'],
  Hand: ['MCP Flexion', 'MCP Extension', 'PIP Flexion', 'PIP Extension', 'DIP Flexion', 'DIP Extension'],
  Thumb: ['CMC Flexion', 'CMC Extension', 'CMC Abduction', 'CMC Adduction', 'MCP Flexion', 'MCP Extension', 'IP Flexion', 'IP Extension', 'Opposition'],
  'Fingers (index/middle/ring/little)': ['MCP Flexion', 'MCP Extension', 'PIP Flexion', 'PIP Extension', 'DIP Flexion', 'DIP Extension'],
  Hip: ['Flexion', 'Extension', 'Abduction', 'Adduction', 'External Rotation', 'Internal Rotation', 'Hip IR/ER seated'],
  Knee: ['Flexion', 'Extension'],
  Ankle: ['Dorsiflexion', 'Plantarflexion', 'Inversion', 'Eversion'],
  Foot: ['Great Toe Extension', 'Great Toe Flexion', 'Lesser Toe Extension', 'Lesser Toe Flexion', 'Midfoot Inversion/Eversion'],
  Toes: ['Great Toe Extension', 'Great Toe Flexion', 'Lesser Toe Extension', 'Lesser Toe Flexion'],
  'Cervical Spine': ['Flexion', 'Extension', 'Rotation (Left)', 'Rotation (Right)', 'Sidebending (Left)', 'Sidebending (Right)'],
  'Thoracic Spine': ['Rotation (Left)', 'Rotation (Right)', 'Flexion', 'Extension', 'Sidebending (Left)', 'Sidebending (Right)'],
  'Lumbar Spine': ['Flexion', 'Extension', 'Sidebending (Left)', 'Sidebending (Right)', 'Rotation (Left)', 'Rotation (Right)'],
  'TMJ (Temporomandibular joint)': ['Depression (mouth opening in mm)', 'Elevation', 'Lateral Deviation Left', 'Lateral Deviation Right', 'Protrusion'],
};

/** MMT: region -> movement -> the muscles that movement's picker offers. */
export const MMT_REGION_MOVEMENT_MUSCLES: Record<string, Record<string, string[]>> = {
  'Cervical Spine': {
    Flexion: ['Deep cervical flexors', 'Sternocleidomastoid', 'Scalenes'],
    Extension: ['Splenius capitis', 'Splenius cervicis', 'Semispinalis capitis', 'Semispinalis cervicis', 'Cervical extensors'],
    'Lateral Flexion': ['Sternocleidomastoid', 'Scalenes', 'Levator scapulae', 'Upper trapezius'],
    Rotation: ['Sternocleidomastoid', 'Splenius capitis', 'Splenius cervicis', 'Deep cervical rotators'],
  },
  Scapula: {
    Elevation: ['Upper trapezius', 'Levator scapulae'],
    Depression: ['Lower trapezius', 'Pectoralis minor', 'Latissimus dorsi'],
    Retraction: ['Middle trapezius', 'Rhomboids'],
    Protraction: ['Serratus anterior', 'Pectoralis minor'],
    'Upward Rotation': ['Upper trapezius', 'Lower trapezius', 'Serratus anterior'],
    'Downward Rotation': ['Rhomboids', 'Levator scapulae', 'Pectoralis minor'],
  },
  Shoulder: {
    Flexion: ['Anterior deltoid', 'Coracobrachialis', 'Pectoralis major clavicular fibers', 'Biceps brachii'],
    Extension: ['Posterior deltoid', 'Latissimus dorsi', 'Teres major', 'Triceps brachii long head'],
    Abduction: ['Middle deltoid', 'Supraspinatus'],
    Adduction: ['Latissimus dorsi', 'Pectoralis major', 'Teres major'],
    'External Rotation': ['Infraspinatus', 'Teres minor', 'Posterior deltoid'],
    'Internal Rotation': ['Subscapularis', 'Pectoralis major', 'Latissimus dorsi', 'Teres major', 'Anterior deltoid'],
    'Horizontal Abduction': ['Posterior deltoid', 'Infraspinatus', 'Teres minor'],
    'Horizontal Adduction': ['Pectoralis major', 'Anterior deltoid', 'Coracobrachialis'],
  },
  Elbow: {
    Flexion: ['Biceps brachii', 'Brachialis', 'Brachioradialis'],
    Extension: ['Triceps brachii', 'Anconeus'],
  },
  Forearm: {
    Supination: ['Biceps brachii', 'Supinator'],
    Pronation: ['Pronator teres', 'Pronator quadratus'],
  },
  Wrist: {
    Flexion: ['Flexor carpi radialis', 'Flexor carpi ulnaris', 'Palmaris longus'],
    Extension: ['Extensor carpi radialis longus', 'Extensor carpi radialis brevis', 'Extensor carpi ulnaris'],
    'Radial Deviation': ['Flexor carpi radialis', 'Extensor carpi radialis longus', 'Extensor carpi radialis brevis'],
    'Ulnar Deviation': ['Flexor carpi ulnaris', 'Extensor carpi ulnaris'],
  },
  'Hand / Fingers': {
    'Finger Flexion': ['Flexor digitorum superficialis', 'Flexor digitorum profundus'],
    'Finger Extension': ['Extensor digitorum', 'Extensor indicis', 'Extensor digiti minimi'],
    'Finger Abduction': ['Dorsal interossei'],
    'Finger Adduction': ['Palmar interossei'],
    'MCP Flexion': ['Lumbricals', 'Interossei'],
    'MCP Extension': ['Extensor digitorum'],
    'PIP Flexion': ['Flexor digitorum superficialis'],
    'DIP Flexion': ['Flexor digitorum profundus'],
  },
  Thumb: {
    Flexion: ['Flexor pollicis longus', 'Flexor pollicis brevis'],
    Extension: ['Extensor pollicis longus', 'Extensor pollicis brevis'],
    Abduction: ['Abductor pollicis longus', 'Abductor pollicis brevis'],
    Adduction: ['Adductor pollicis'],
    Opposition: ['Opponens pollicis'],
  },
  'Thoracic / Lumbar / Trunk': {
    Flexion: ['Rectus abdominis', 'External oblique', 'Internal oblique'],
    Extension: ['Erector spinae', 'Multifidi'],
    'Lateral Flexion': ['Quadratus lumborum', 'Internal oblique', 'External oblique', 'Erector spinae'],
    Rotation: ['Internal oblique', 'External oblique', 'Multifidi'],
  },
  Hip: {
    Flexion: ['Iliopsoas', 'Rectus femoris', 'Sartorius', 'Tensor fasciae latae'],
    Extension: ['Gluteus maximus', 'Hamstrings'],
    Abduction: ['Gluteus medius', 'Gluteus minimus', 'Tensor fasciae latae'],
    Adduction: ['Adductor longus', 'Adductor brevis', 'Adductor magnus', 'Gracilis', 'Pectineus'],
    'External Rotation': ['Gluteus maximus', 'Piriformis', 'Obturator internus', 'Obturator externus', 'Gemellus superior', 'Gemellus inferior', 'Quadratus femoris'],
    'Internal Rotation': ['Tensor fasciae latae', 'Gluteus medius anterior fibers', 'Gluteus minimus anterior fibers'],
  },
  Knee: {
    Flexion: ['Hamstrings', 'Sartorius', 'Gracilis', 'Gastrocnemius'],
    Extension: ['Quadriceps femoris'],
  },
  Ankle: {
    Dorsiflexion: ['Tibialis anterior', 'Extensor hallucis longus', 'Extensor digitorum longus'],
    Plantarflexion: ['Gastrocnemius', 'Soleus', 'Plantaris'],
    Inversion: ['Tibialis posterior', 'Tibialis anterior'],
    Eversion: ['Fibularis longus', 'Fibularis brevis', 'Fibularis tertius'],
  },
  'Foot / Toes': {
    'Great Toe Extension': ['Extensor hallucis longus'],
    'Great Toe Flexion': ['Flexor hallucis longus'],
    'Toe Extension': ['Extensor digitorum longus', 'Extensor digitorum brevis'],
    'Toe Flexion': ['Flexor digitorum longus', 'Flexor digitorum brevis'],
  },
};

/** Special Tests: body region -> the tests that region offers. */
export const SPECIAL_TESTS_BY_REGION: Record<string, string[]> = {
  Shoulder: ['Hawkins-Kennedy Test', 'Neer\'s Test', 'O\'Brien\'s Test', 'Empty Can Test (Jobe\'s)', 'Drop Arm Test', 'Apprehension Test', 'Relocation Test', 'Speed\'s Test', 'Yergason\'s Test', 'Cross-Body Adduction Test'],
  Elbow: ['Varus Stress Test', 'Valgus Stress Test', 'Cozen\'s Test', 'Mill\'s Test', 'Tinel\'s Sign (Elbow)', 'Golfer\'s Elbow Test'],
  'Wrist/Hand': ['Phalen\'s Test', 'Tinel\'s Sign (Wrist)', 'Finkelstein\'s Test', 'Watson\'s Test', 'Allen Test', 'Froment\'s Sign'],
  'Cervical Spine': ['Spurling\'s Test', 'Distraction Test', 'Compression Test', 'Lhermitte\'s Sign', 'Sharp-Purser Test', 'Vertebral Artery Test'],
  'Lumbar Spine': ['Straight Leg Raise (SLR)', 'Slump Test', 'Femoral Nerve Stretch Test', 'FABER Test', 'Gaenslen\'s Test', 'Stork Test (Single Leg Stance)'],
  Hip: ['FABER (Patrick\'s) Test', 'FADIR Test', 'Thomas Test', 'Ober\'s Test', 'Trendelenburg Test', 'Log Roll Test', 'Scour Test'],
  Knee: ['Lachman Test', 'Anterior Drawer Test', 'Posterior Drawer Test', 'Valgus Stress Test', 'Varus Stress Test', 'McMurray\'s Test', 'Apley\'s Compression Test', 'Patellar Apprehension Test', 'Noble\'s Test', 'Thessaly Test'],
  'Ankle/Foot': ['Anterior Drawer Test', 'Talar Tilt Test', 'Thompson Test (Achilles)', 'Squeeze Test', 'External Rotation Test', 'Kleiger Test', 'Tinel\'s Sign (Ankle)', 'Morton\'s Test'],
};
